/**
 * §7.7 notification centre. Two visually distinct, separately filterable classes.
 * Action-required items carry no dismiss control here — they clear when the thing
 * they demand is done, and they are mirrored as a banner on the relevant screen.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNotifications } from '../hooks/useNotifications';
import { localDateTime } from '../lib/format';
import { EmptyState } from './EmptyState';
import { Loading } from './ui';

type Filter = 'all' | 'action_required' | 'informational';

export function NotificationBell({ onOpen }: { onOpen: () => void }) {
  const { unread, unreadActionRequired } = useNotifications();
  const label =
    unread === 0
      ? 'Notifications, none unread'
      : `Notifications, ${unread} unread${unreadActionRequired ? `, ${unreadActionRequired} needing action` : ''}`;

  return (
    <button type="button" className="btn btn-ghost bell" onClick={onOpen} aria-label={label}>
      <span aria-hidden="true">Alerts</span>
      {unread > 0 ? (
        <span className={`bell-count${unreadActionRequired > 0 ? ' bell-count-action' : ''}`}>
          {unread}
        </span>
      ) : null}
    </button>
  );
}

export function NotificationPanel({ onClose }: { onClose: () => void }) {
  const { items, loading, markRead, markAllRead } = useNotifications();
  const [filter, setFilter] = useState<Filter>('all');

  const shown = items.filter((n) => filter === 'all' || n.priority === filter);

  return (
    <div className="notif-panel" role="region" aria-label="Notifications">
      <header className="notif-head">
        <div className="notif-filters" role="group" aria-label="Filter notifications">
          {(['all', 'action_required', 'informational'] as Filter[]).map((f) => (
            <button
              key={f}
              type="button"
              className={`chip${filter === f ? ' chip-on' : ''}`}
              aria-pressed={filter === f}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'action_required' ? 'Action required' : 'Informational'}
            </button>
          ))}
        </div>
        <div className="notif-head-actions">
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => void markAllRead()}>
            Mark informational read
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
            Close
          </button>
        </div>
      </header>

      {loading && items.length === 0 ? <Loading label="Loading notifications" /> : null}

      {shown.length === 0 && !loading ? (
        <EmptyState heading="Nothing here" body="No notifications in this filter." />
      ) : (
        <ul className="notif-list">
          {shown.map((n) => (
            <li
              key={n.id}
              className={`notif notif-${n.priority}${n.read_at ? ' notif-read' : ''}`}
            >
              <div className="notif-body">
                <span className="notif-class">
                  {n.priority === 'action_required' ? 'Action required' : 'For information'}
                </span>
                <strong>{n.title}</strong>
                {n.body ? <p>{n.body}</p> : null}
                <p className="muted">{localDateTime(n.created_at)}</p>
              </div>
              <div className="notif-actions">
                {n.link ? (
                  <Link className="btn btn-sm btn-primary" to={n.link} onClick={onClose}>
                    Open
                  </Link>
                ) : null}
                {/* Informational items dismiss; action-required ones do not (§7.7). */}
                {n.priority === 'informational' && !n.read_at ? (
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => void markRead(n.id)}
                  >
                    Dismiss
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * The screen-level mirror of an action-required item (§7.7). Rendered in the
 * header of the screen the item is about, so acting on it never requires opening
 * the centre first.
 */
export function ActionRequiredBanners({
  scope,
}: {
  scope: { botId?: string; rentalId?: string };
}) {
  const { actionRequiredFor } = useNotifications();
  const items = actionRequiredFor(scope);
  if (items.length === 0) return null;

  return (
    <div className="action-required-stack">
      {items.map((n) => (
        <div key={n.id} className="banner banner-action" role="alert">
          <div className="banner-text">
            <strong className="banner-title">{n.title}</strong>
            {n.body ? <div className="banner-body">{n.body}</div> : null}
          </div>
          {n.link ? (
            <div className="banner-actions">
              <Link className="btn btn-primary btn-sm" to={n.link}>
                Resolve
              </Link>
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}
